import { call, put, SagaGenerator, select, takeLatest } from "typed-redux-saga";
import { Action } from "typescript-fsa";
import { isString } from "generic-type-guard";

import { ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { Model, ModelSelectors } from "@com.mgmtp.a12.client/client-core/lib/core/model";
import { GroupInstance, DocumentServiceFactory } from "@com.mgmtp.a12.kernel/kernel-md-facade";
import { OverviewActivity } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/client-extensions";

import { createCustomNotificationAction } from "../../app/actions";
import { localizableFromLocaleResources, RESOURCE_KEYS } from "../../localization";
import { assertNonNullable, assertTrue } from "../../utilities/assert";
import { getDocument, modifyDocument } from "../../utilities/rpcHelper";

import {
	didUpdateOverviewDocument,
	editOverviewDocument,
	EditOverviewDocumentPayload,
	updateOverviewDocument
} from "./actions";

const documentService = new DocumentServiceFactory().getDocumentService();

/**
 * Zeigt das Modal zum Anlegen eines neuen oder Editieren eines bestehenden Dokumentes in der AuxiliaryOverviewEngine.
 */
export function* editOverviewDocumentSaga(): SagaGenerator<void> {
	yield* takeLatest(editOverviewDocument, showEditOverviewDocumentModal);
}

function* showEditOverviewDocumentModal(
	editOverviewDocumentAction: Action<EditOverviewDocumentPayload>
): SagaGenerator<void> {
	const activity = yield* select(ActivitySelectors.activityById(editOverviewDocumentAction.payload.activityId));
	if (activity === undefined) {
		throw new Error(
			`Activity [id: ${editOverviewDocumentAction.payload.activityId}] does not exist in the store anymore.`
		);
	}

	const activityDescriptor = {
		...activity.descriptor,
		instance: editOverviewDocumentAction.payload.instanceId
	};

	//Nur im readonly Modus soll ein Row-click zum öffnen des forms führen
	if (editOverviewDocumentAction.payload.srcEvent === "ROW_CLICK" && !editOverviewDocumentAction.payload.readonly) {
		return;
	}

	const createActivity = ActivityActions.create({
		activityDescriptor,
		initiatingActivityId: editOverviewDocumentAction.payload.activityId,
		slices: {
			uiState: {
				readonly: editOverviewDocumentAction.payload.readonly
			}
		}
	});

	yield* put(createActivity);
}

export function* updateOverviewDocumentSaga(): SagaGenerator<void> {
	yield* takeLatest(
		updateOverviewDocument,
		function* handler({ payload: { activityId, originalDocument, updates } }) {
			const activity = yield* select(ActivitySelectors.activityById(activityId));
			assertNonNullable(activity);

			const documents = yield* select(
				state =>
					(ActivitySelectors.data(activity.id)(state) as OverviewActivity.Data.DocumentListData)?.documents
			);
			assertNonNullable(documents);

			const documentModel = yield* select(
				ModelSelectors.modelInScene(
					{ activityId: activity.id, modelType: "document" },
					Model.isDocumentAndValidationModel
				)
			);
			assertNonNullable(documentModel);

			// A preliminary document with the changed value applied. Note that we cannot use this to actually commit the change
			// since the document as retrieved by the overview engine may not be completed (document list fragment)!
			let preliminaryDocument: GroupInstance | undefined = undefined;
			for (let i = 0; i < updates.length; i++) {
				preliminaryDocument = documentService.updateEntityInstance(
					preliminaryDocument ?? originalDocument,
					updates[i][0],
					updates[i][1],
					documentModel
				);
			}
			if (!preliminaryDocument) {
				preliminaryDocument = originalDocument;
			}

			// Use optimistic UI updates
			yield* put(
				ActivityActions.setData({
					activityId,
					data: {
						documents: documents.map(it => (it?.id === preliminaryDocument!.id ? preliminaryDocument : it))
					}
				})
			);

			let serverDocument: GroupInstance | undefined;
			try {
				const docSpec = yield* call(getDocument, originalDocument.id as string);
				assertNonNullable(docSpec);
				serverDocument = documentService.parseDates(docSpec.document, documentModel) as GroupInstance;
			} catch {
				yield* put(
					createCustomNotificationAction({
						message: localizableFromLocaleResources(
							RESOURCE_KEYS.features.notification.error.wiedervorlagenErledigt
						),
						severity: "error",
						group: `updateOverviewDocument:${originalDocument.id}`
					})
				);
				// Restore state to before the failed modification
				yield* put(
					ActivityActions.setData({
						activityId: activity.id,
						data: {
							documents: documents.map(it => (it?.id === originalDocument.id ? originalDocument : it))
						}
					})
				);
				return;
			}

			assertNonNullable(serverDocument);

			let updatedDocument: GroupInstance | undefined = undefined;
			for (let i = 0; i < updates.length; i++) {
				updatedDocument = documentService.updateEntityInstance(
					updatedDocument ?? serverDocument,
					updates[i][0],
					updates[i][1],
					documentModel
				);
			}

			assertNonNullable(updatedDocument);

			const formattedDocument = documentService.formatDates(updatedDocument, documentModel) as Record<
				string,
				object | string | boolean | number | Date
			>;

			try {
				assertTrue(isString(originalDocument.id));
				yield* call(modifyDocument, originalDocument.id, formattedDocument);
			} catch {
				yield* put(
					createCustomNotificationAction({
						message: localizableFromLocaleResources(
							RESOURCE_KEYS.features.notification.error.wiedervorlagenErledigt
						),
						severity: "error",
						group: `updateOverviewDocument:${originalDocument.id}`
					})
				);
				// Restore state to before the failed modification
				yield* put(
					ActivityActions.setData({
						activityId: activity.id,
						data: {
							documents: documents.map(it => (it?.id === originalDocument.id ? originalDocument : it))
						}
					})
				);
				return;
			}

			yield* put(
				didUpdateOverviewDocument({
					activityId,
					updatedDocument: { ...updatedDocument, modelId: originalDocument.modelId, id: originalDocument.id }
				})
			);
		}
	);
}
export const overviewDocumentSagas = [editOverviewDocumentSaga, updateOverviewDocumentSaga];
